import { faLayerGroup } from "@fortawesome/pro-duotone-svg-icons"
import useServiceTicketsEnabled from '../../hooks/useServiceTicketsEnabled'
import { faChevronLeft, faChevronRight, faTimes, faPlus } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Input, Typography } from "antd"
import { useState, useEffect } from "react"
import { useAccountSetup } from "./AccountSetupContext"
import { useDivisions, useSaveDivisionMutation, useDeleteDivisionMutation } from "../../domain/divisions/divisionsQueries"
import { availableJobTypes } from "./accountSetupReducer"
import styled from "styled-components"
import './AccountSetupModal.css'

const { Title, Text } = Typography

export type Props = {
  onBack: () => void
  onExit: () => void
}

const DivisionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`

const DivisionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const DivisionInputDisplay = styled.div`
  flex: 1;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
`

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #999;
  transition: color 0.2s;
  font-size: 16px;

  &:hover {
    color: #e85d04;
  }
`

const AddDivisionButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  color: #666;
  font-size: 14px;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    border-color: ${props => props.disabled ? '#d9d9d9' : '#e85d04'};
    color: ${props => props.disabled ? '#666' : '#e85d04'};
  }
`

const AccountSetupDivisionsStep = ({ onBack, onExit }: Props) => {
  const jobTypesForTenant = availableJobTypes(useServiceTicketsEnabled())
  const { state, dispatch } = useAccountSetup()
  const { divisions } = state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isSavingAll, setIsSavingAll] = useState(false)

  // API hooks
  const { data: existingDivisions } = useDivisions()
  const saveDivision = useSaveDivisionMutation()
  const deleteDivision = useDeleteDivisionMutation()

  const isSaving = saveDivision.isLoading || deleteDivision.isLoading || isSavingAll

  // Merge existing API divisions with wizard defaults
  useEffect(() => {
    if (!existingDivisions) return

    // Get unique division names from API (divisions can have multiple types per name)
    const apiDivisionNames = new Set(existingDivisions.map(d => d.name.toLowerCase()))

    // Get current wizard default names
    const wizardDefaultNames = new Set(divisions.map(d => d.name.toLowerCase()))

    // Find API divisions that aren't already in wizard defaults (custom divisions)
    const customDivisions = [...apiDivisionNames]
      .filter(name => !wizardDefaultNames.has(name))
      .map(name => {
        const apiDiv = existingDivisions.find(d => d.name.toLowerCase() === name)
        return { id: String(apiDiv!.id), name: apiDiv!.name, jobTypes: jobTypesForTenant }
      })

    // Only update if there are custom divisions to add
    if (customDivisions.length > 0) {
      dispatch({ type: 'SET_DIVISIONS', payload: [...divisions, ...customDivisions] })
    }
  }, [existingDivisions])

  const handleNext = async () => {
    setIsSavingAll(true)
    try {
      // For each division in wizard state, create API divisions for the relevant job types
      for (const division of divisions) {
        const toCreate = []
        for (const jobType of division.jobTypes) {
          const alreadyExists = existingDivisions?.some(
            d => d.name === division.name && d.type === jobType
          )
          if (!alreadyExists) {
            toCreate.push(saveDivision.mutateAsync({
              id: 0,
              name: division.name,
              type: jobType,
              groupName: '',
              schedulable: true
            }))
          }
        }
        if (toCreate.length > 0) {
          await Promise.all(toCreate)
        }
      }

      dispatch({ type: 'NEXT_STEP' })
    } finally {
      setIsSavingAll(false)
    }
  }

  const handleRemoveDivision = async (id: string, name: string) => {
    // Find all divisions with this name (all 3 job types) and delete them
    if (existingDivisions) {
      const divisionsToDelete = existingDivisions.filter(d => d.name === name)
      if (divisionsToDelete.length > 0) {
        await Promise.all(
          divisionsToDelete.map(d => deleteDivision.mutateAsync(d.id))
        )
      }
    }
    dispatch({ type: 'REMOVE_DIVISION', payload: id })
  }

  const handleAddDivision = () => {
    const tempId = 'new-' + Date.now()
    dispatch({ type: 'ADD_DIVISION', payload: { id: tempId, name: '', jobTypes: jobTypesForTenant } })
    setEditingId(tempId)
  }

  const handleUpdateDivisionName = (id: string, name: string) => {
    dispatch({ type: 'UPDATE_DIVISION', id, name })
  }

  const handleFinishEditing = (id: string, name: string) => {
    if (!name.trim()) {
      // Remove empty divisions
      dispatch({ type: 'REMOVE_DIVISION', payload: id })
    }
    setEditingId(null)
  }

  const handleKeyPress = (e: React.KeyboardEvent, id: string, name: string) => {
    if (e.key === 'Enter') {
      handleFinishEditing(id, name)
    } else if (e.key === 'Escape') {
      if (!name.trim()) {
        dispatch({ type: 'REMOVE_DIVISION', payload: id })
      }
      setEditingId(null)
    }
  }

  return (
    <div className="account-setup-modal-content">
      <div className="account-setup-progress-bar">
        <div className="account-setup-progress-fill" style={{ width: '62.5%' }} />
      </div>
      <button
        className="account-setup-exit-button"
        onClick={onExit}
        aria-label="Exit setup"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className="account-setup-modal-left account-setup-form-container">
        <div className="account-setup-form-header">
          <div className="account-setup-modal-icon">
            <FontAwesomeIcon icon={faLayerGroup} size="2x" />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            What Divisions Do You Work With?
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Organize your divisions. Divisions help you group work by specialty or service type. We've added a few common ones based on your services to get you started.
          </Text>
        </div>

        <div className="account-setup-form" style={{ marginTop: 24 }}>
          <DivisionsList>
            {divisions.map((division) => (
              <DivisionRow key={division.id}>
                {editingId === division.id ? (
                  <Input
                    placeholder="Division name"
                    value={division.name}
                    onChange={(e) => handleUpdateDivisionName(division.id, e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, division.id, division.name)}
                    onBlur={() => handleFinishEditing(division.id, division.name)}
                    autoFocus
                    size="large"
                    disabled={isSaving}
                    style={{ flex: 1 }}
                  />
                ) : (
                  <DivisionInputDisplay>{division.name}</DivisionInputDisplay>
                )}
                <RemoveButton
                  onClick={() => handleRemoveDivision(division.id, division.name)}
                  aria-label={`Remove ${division.name}`}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </RemoveButton>
              </DivisionRow>
            ))}
          </DivisionsList>

          <AddDivisionButton onClick={handleAddDivision} disabled={isSaving}>
            <FontAwesomeIcon icon={faPlus} />
            Add {divisions.length === 0 ? 'a' : 'Another'} Division
          </AddDivisionButton>

          <div className="account-setup-form-buttons" style={{ marginTop: 48 }}>
            <Button size="large" onClick={onBack} disabled={isSaving}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              loading={isSavingAll}
              disabled={isSaving}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>
      </div>

      <div className="account-setup-modal-right">
        <img
          src="/images/account-setup/step-5.png"
          alt="Divisions setup"
          className="account-setup-modal-image"
        />
        <div className="account-setup-modal-logo">
          <img src="/images/synkedup-dark.png" alt="SynkedUP" />
        </div>
        <div className="account-setup-modal-testimonial-wrapper">
          <div className="account-setup-modal-testimonial">
            <Text className="account-setup-testimonial-quote">
              "The old school method of hand written estimates took 2 hours, and now takes 10-15 mins in SynkedUP"
            </Text>
          </div>
          <div className="account-setup-testimonial-author">
            <Text strong style={{ color: 'white' }}>Brian Schmaedeke</Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: 14 }}>
              Solstice Landscapes
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountSetupDivisionsStep
